"use client"

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import Link from "next/link"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/lib/intl/use-translations"
import { useAiAnswer } from "@/components/chat/use-ai-answer"
import { useChatSession } from "@/components/chat/use-chat-session"
import { useChatLauncherContext } from "@/components/chat/chat-launcher-context"
import { ChatMessage } from "@/components/chat/chat-message"
import { EscalationAction } from "@/components/chat/escalation-action"
import {
  useExperimentVariantNoExposure,
  useReportExperimentExposure,
} from "@/components/analytics/experiment"
import { EXPERIMENTS } from "@/lib/experiments"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { blobMediaUrl } from "@/lib/blob-url"
import { track } from "@/lib/analytics"
import { SparkleIcon } from "@/components/ui/icons"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"

export interface EllaTestSectionProps {
  scope: string
  country: string
  locale: string
  pageUrl: string
  pageTitle: string
  testName: string
  bookingCta: React.ReactNode
}

export const ELLA_TEST_SECTION_FLAG = EXPERIMENTS["test-detail-ella-v1"].flag

/** The arm that renders the section. `control` renders nothing — the page as it
 *  ships today. */
export const ELLA_TEST_SECTION_ARM = "test"

/** A starter / follow-up question chip. Same affordance before the first
 *  question and after an answer, so the two rows can't drift apart. */
function PromptChip({
  prompt,
  onSelect,
}: {
  prompt: string
  onSelect: () => void
}): React.ReactNode {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="type-body-md text-deep-blue-950 rounded-full border border-neutral-200 bg-white px-4 py-2.5 font-medium transition-colors hover:bg-neutral-100"
    >
      {prompt}
    </button>
  )
}

// Team faces (contact page), overlapping — the human alternative to the AI.
const TEAM_AVATARS = [
  "/images/avatars/laura.png",
  "/images/team/ferdinand.png",
  "/images/team/nick.png",
  "/images/team/dennis.png",
]

/**
 * Under-fold AI-assistant section on test detail pages (test arm of
 * `test-detail-ella-v1`). The variant gate lives in this thin wrapper so the
 * chat hooks in {@link EllaTestSectionBody} always run in a stable order.
 *
 * The arm is edge-assigned — read synchronously from the
 * `ahead-experiment-assignments` cookie, because the PostHog client SDK serves
 * no live flags on this site and a plain client read resolves `undefined` for
 * everyone (AHD-9499). That cookie short-circuits posthog-js's auto-exposure,
 * so the exposure is fired explicitly here.
 *
 * **This component IS the exposure surface**, so its parent must mount it only
 * for the population that can see either arm — `page.tsx` gates the mount to
 * non-DE, non-waitlist-only markets. The reported arm is resolved from the
 * cookie inside the hook rather than from `variant`: a stale render snapshot
 * must never reach the exposure (AHD-8246, see `resolveExposureArm`).
 */
export function EllaTestSection(props: EllaTestSectionProps): React.ReactNode {
  const variant = useExperimentVariantNoExposure(ELLA_TEST_SECTION_FLAG)
  useReportExperimentExposure(ELLA_TEST_SECTION_FLAG, true)
  if (variant !== ELLA_TEST_SECTION_ARM) return null
  return <EllaTestSectionBody {...props} />
}

function EllaTestSectionBody({
  scope,
  country,
  locale,
  pageUrl,
  pageTitle,
  testName,
  bookingCta,
}: EllaTestSectionProps): React.ReactNode {
  const t = useTranslations("chat")
  const localePath = useLocalePath()
  const { setPanelOpen } = useChatSession({ country, locale })
  const { requestPanelOpen } = useChatLauncherContext()
  const { turns, isStreaming, error, status, handedOff, ask } = useAiAnswer({
    scope,
    country,
    locale,
    pageUrl,
    pageTitle,
  })

  const hasTurns = turns.length > 0
  const answered = hasTurns && !isStreaming

  // Continue in the full panel: `requestPanelOpen()` opens the mounted
  // ContextualChat on this page view, `setPanelOpen` keeps it open across a
  // remount. That panel is the only surface with the live Chatwoot
  // subscription, so it's where a human's replies actually arrive.
  const openLauncher = useCallback(() => {
    setPanelOpen(true)
    requestPanelOpen()
  }, [setPanelOpen, requestPanelOpen])

  // Transient progress line for the in-flight bot turn (mirrors ai-answer-lane).
  const statusLabel = ((): string | undefined => {
    if (!status) return undefined
    if (
      status.phase === "searching" &&
      status.index != null &&
      status.total != null
    ) {
      return t("status.searching", {
        index: String(status.index),
        total: String(status.total),
      })
    }
    if (status.phase === "searching") return t("status.retrieving")
    if (status.phase === "understanding") return t("status.understanding")
    return t("status.retrieving")
  })()

  // Fire the "offered" analytics once when the assistant first surfaces.
  const offered = useRef(false)
  useEffect(() => {
    if (offered.current) return
    offered.current = true
    track.aiAnswerOffered({
      surface: "detail",
      scope,
      intent: "question",
      query_length: 0,
    })
  }, [scope])

  // Prompts already asked, so a chip the visitor used drops out of the
  // follow-up row instead of inviting the same question twice.
  const [askedPrompts, setAskedPrompts] = useState<string[]>([])

  const submit = useCallback(
    // `promptKey` set only on chip clicks — travels to glue as trace
    // provenance so review tooling can tell canned questions from typed ones.
    (message: string, promptKey?: string) => {
      const trimmed = message.trim()
      if (!trimmed) return
      track.aiAnswerRequested({
        surface: "detail",
        scope,
        intent: "question",
        query_length: trimmed.length,
      })
      setAskedPrompts((prev) =>
        prev.includes(trimmed) ? prev : [...prev, trimmed]
      )
      void ask(trimmed, promptKey)
    },
    [ask, scope]
  )

  // ── Thread scrolling ──
  // The card lives in a page section, so an unbounded thread pushes every
  // section below it down the page. Cap the height and scroll inside, keeping
  // the newest turn in view — unless the visitor has scrolled up to re-read,
  // in which case yanking them back to the bottom is worse than a stale view.
  const threadRef = useRef<HTMLDivElement | null>(null)
  const pinnedToBottom = useRef(true)
  const onThreadScroll = useCallback(() => {
    const el = threadRef.current
    if (!el) return
    pinnedToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 48
  }, [])
  useEffect(() => {
    const el = threadRef.current
    if (!el || !pinnedToBottom.current) return
    el.scrollTop = el.scrollHeight
  }, [turns])

  // Own-input composer (model on ai-answer-lane's `ownInput`).
  const [draft, setDraft] = useState("")
  const submitDraft = useCallback(() => {
    submit(draft)
    setDraft("")
  }, [submit, draft])
  const onDraftKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        submitDraft()
      }
    },
    [submitDraft]
  )

  const lastUserMessage = (() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "user") return turns[i].content
    }
    return ""
  })()

  // Starter prompts for this scope, falling back to the generic section set.
  // `t.raw`/`t.has` are narrowed to known keys by the generated message types,
  // so cast to a string-key signature for the dynamic scope lookup (mirrors
  // usePromptVariant).
  const rawRead = t.raw as (key: string) => unknown
  const hasKey = t.has as (key: string) => boolean
  const promptsKey = `scopes.${scope}.prompts`
  const prompts = (
    hasKey(promptsKey) ? rawRead(promptsKey) : rawRead("section.prompts")
  ) as string[]

  // Follow-ups after an answer: the prompts this visitor hasn't used yet. Three
  // at most — the row sits under an answer, competing with the booking CTA.
  const followUpPrompts = prompts
    .filter((p) => !askedPrompts.includes(p))
    .slice(0, 3)

  return (
    <section
      id="ella-test-section"
      data-testid="ella-test-section"
      className="scroll-mt-24 border-y border-neutral-200 bg-gradient-to-b from-neutral-100 to-neutral-50 py-20 md:py-28"
    >
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[0.85fr_1.1fr] lg:gap-16">
        {/* left — topic-first framing; the team is one way to get answers */}
        <div className="max-w-md">
          <h2 className="type-display-sm text-neutral-900">
            {t("section.heading", { testName })}
          </h2>
          <p className="type-body-lg mt-4 text-neutral-600">
            {t("section.subtitle")}
          </p>
          <div className="mt-8 flex items-center gap-3">
            <div className="flex -space-x-3">
              {TEAM_AVATARS.map((src) => (
                <Image
                  key={src}
                  src={blobMediaUrl(src)}
                  alt=""
                  width={44}
                  height={44}
                  className="h-11 w-11 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
            <span className="type-body-sm text-neutral-500">
              {t("section.teamCaption")}
            </span>
          </div>
          <div className="mt-6">
            <Link
              href={localePath("/contact")}
              className={cn(
                buttonVariants({ variant: "outline" }),
                "border-deep-blue-950 text-deep-blue-950 hover:bg-deep-blue-950/5 bg-white"
              )}
            >
              {t("section.talkToTeam")}
            </Link>
          </div>
        </div>

        {/* right — the AI assistant, titled by what it does (not a name) */}
        <div className="overflow-hidden rounded-[20px] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_30px_70px_-32px_rgba(24,16,26,0.28)]">
          <div className="flex items-start justify-between gap-3 border-b border-neutral-100 px-6 py-5">
            <div>
              <h3 className="type-title-lg text-neutral-900">
                {t("section.cardTitle")}
              </h3>
              <p className="type-body-sm mt-1.5 text-neutral-500">
                {t("section.cardSubtitle")}
              </p>
            </div>
            <SparkleIcon
              width={20}
              height={20}
              className="mt-0.5 shrink-0 text-neutral-400"
            />
          </div>

          <div className="px-6 pt-5">
            {hasTurns ? (
              <>
                <div
                  ref={threadRef}
                  onScroll={onThreadScroll}
                  data-testid="ella-thread"
                  className="flex max-h-[min(60vh,26rem)] min-h-[140px] flex-col gap-3 overflow-y-auto overscroll-contain pr-1"
                >
                  {turns.map((turn, i) => (
                    <ChatMessage
                      key={turn.messageId ?? `${turn.role}-${i}`}
                      role={turn.role}
                      content={turn.content}
                      isStreaming={turn.isStreaming}
                      variant="bare"
                      thinkingLabel={turn.isStreaming ? statusLabel : undefined}
                      sources={turn.sources}
                      sourcesLabel={t("ui.sources")}
                    />
                  ))}
                </div>

                {/* A human owns the conversation now, and this card has no live
                    subscription — say where the reply will land and open the
                    panel that receives it. */}
                {handedOff && (
                  <div className="mt-4 rounded-[14px] bg-neutral-50 px-4 py-3">
                    <p className="type-body-sm text-neutral-600">
                      {t("ui.connecting_team")}
                    </p>
                    <div className="mt-2">
                      <EscalationAction
                        surface="detail"
                        scope={scope}
                        onContinueInChat={openLauncher}
                      />
                    </div>
                  </div>
                )}

                {answered && (
                  <div className="mt-4 flex flex-col gap-4">
                    {followUpPrompts.length > 0 && (
                      <div>
                        <p className="type-body-sm text-neutral-500">
                          {t("section.askAnother")}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2.5">
                          {followUpPrompts.map((s) => (
                            <PromptChip
                              key={s}
                              prompt={s}
                              onSelect={() =>
                                // Index into the master list, not this
                                // filtered row — stable across asked-filtering.
                                submit(
                                  s,
                                  `${scope}.inline-followup.${prompts.indexOf(s)}`
                                )
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                      {bookingCta}
                      {!handedOff && (
                        <EscalationAction
                          surface="detail"
                          scope={scope}
                          onContinueInChat={openLauncher}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex min-h-[140px] flex-col gap-5">
                <div className="flex items-start gap-2.5">
                  <div className="type-body-md max-w-[92%] rounded-[14px] rounded-bl-[4px] bg-neutral-50 px-4 py-3 text-neutral-900">
                    {t("section.intro")}
                  </div>
                </div>
                {prompts.length > 0 && (
                  <div className="flex flex-wrap gap-2.5">
                    {prompts.map((s, i) => (
                      <PromptChip
                        key={s}
                        prompt={s}
                        onSelect={() => submit(s, `${scope}.inline.${i}`)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 pt-5 pb-6">
            <div className="flex items-center gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onDraftKey}
                placeholder={t("section.placeholder")}
                aria-label={t("section.inputAria")}
                className="type-body-md min-h-[44px] flex-1 rounded-full border border-neutral-200 bg-white px-4 text-neutral-900 transition-colors outline-none placeholder:text-neutral-400 focus:border-neutral-400"
              />
              <button
                type="button"
                onClick={submitDraft}
                disabled={isStreaming}
                aria-label={t("section.ask")}
                className="bg-deep-blue-950 grid h-[44px] w-[44px] shrink-0 place-items-center rounded-full text-white transition-[box-shadow,transform] duration-200 hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.12)] active:scale-[0.97] disabled:opacity-40"
              >
                <ArrowRightIcon size={18} />
              </button>
            </div>
            {error && (
              <button
                type="button"
                onClick={() => lastUserMessage && submit(lastUserMessage)}
                className="type-body-md mt-3 font-medium text-neutral-900 underline underline-offset-4"
              >
                {t("assistant.retry")}
              </button>
            )}
            <p className="type-body-sm mt-3 text-neutral-500">
              {t("section.disclaimer")}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
